// src/pages/ItemMaster.jsx
import React, { useEffect, useState, useRef } from "react";
import { collection, addDoc, getDocs, updateDoc, deleteDoc, doc, writeBatch } from "firebase/firestore";
import { db } from "../firebase";
import * as XLSX from "xlsx";

/**
 * ItemMaster
 * - List items
 * - Create / Edit modal
 * - Delete
 * - Bulk Import (xlsx/csv) -> maps columns (code, desc, unit, rate, mrp, gst, stock)
 * - Export CSV
 *
 * Assumes `items` collection exists.
 */

function CSVLink_download(filename, text) {
  const blob = new Blob([text], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function ItemMaster() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const codeRef = useRef(null);

  // form state
  const [code, setCode] = useState("");
  const [desc, setDesc] = useState("");
  const [unit, setUnit] = useState("");
  const [rate, setRate] = useState("");
  const [mrp, setMrp] = useState("");
  const [gst, setGst] = useState(12);
  const [stock, setStock] = useState(0);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, "items"));
      setItems(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("load items", err);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setCode(""); setDesc(""); setUnit(""); setRate(""); setMrp(""); setGst(12); setStock(0);
    setShowModal(true);
    setTimeout(() => codeRef.current?.focus?.(), 60);
  };

  const openEdit = (it) => {
    setEditing(it);
    setCode(it.code || ""); setDesc(it.desc || ""); setUnit(it.unit || ""); setRate(it.rate ?? ""); setMrp(it.mrp ?? ""); setGst(it.gst ?? 12); setStock(it.stock ?? 0);
    setShowModal(true);
    setTimeout(() => codeRef.current?.focus?.(), 60);
  };

  const save = async () => {
    if (!code.trim() || !desc.trim()) return alert("Enter code and description");
    setSaving(true);
    try {
      const payload = {
        code: code.trim(),
        desc: desc.trim(),
        unit: unit.trim(),
        rate: Number(rate) || 0,
        mrp: Number(mrp) || 0,
        gst: Number(gst) || 0,
        stock: Number(stock) || 0,
        updatedAt: new Date().toISOString()
      };
      if (editing) {
        await updateDoc(doc(db, "items", editing.id), payload);
        alert("Updated");
      } else {
        payload.createdAt = new Date().toISOString();
        await addDoc(collection(db, "items"), payload);
        alert("Created");
      }
      setShowModal(false);
      load();
    } catch (err) {
      console.error("save item", err);
      alert("Save failed - check console");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (it) => {
    if (!window.confirm(`Delete item ${it.code} - ${it.desc}?`)) return;
    try {
      await deleteDoc(doc(db, "items", it.id));
      alert("Deleted");
      load();
    } catch (err) {
      console.error("delete item", err);
      alert("Delete failed");
    }
  };

  /* Bulk import */
  const handleFile = async (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });
        if (!json || !json.length) return alert("Empty or invalid file");

        // Normalize & map
        const mapped = json.map(row => {
          const norm = {};
          Object.keys(row).forEach(k => { norm[k.toString().toLowerCase().trim()] = row[k]; });
          return {
            code: String(norm.code || norm.itemcode || norm["item code"] || "").trim(),
            desc: String(norm.desc || norm.description || norm.name || "").trim(),
            unit: String(norm.unit || norm.uom || "").trim(),
            rate: Number(norm.rate || norm.price || 0) || 0,
            mrp: Number(norm.mrp || 0) || 0,
            gst: Number(norm.gst || norm.tax || 12) || 12,
            stock: Number(norm.stock || norm.qty || 0) || 0
          };
        }).filter(r => r.code && r.desc);

        if (!mapped.length) return alert("No valid rows found (need code+desc)");

        // Confirm before writing
        if (!window.confirm(`Found ${mapped.length} valid rows. Proceed to import to Firestore?`)) return;

        // Batch write in chunks of 300
        const CHUNK = 300;
        let written = 0;
        for (let i=0; i<mapped.length; i += CHUNK) {
          const chunk = mapped.slice(i, i+CHUNK);
          const batch = writeBatch(db);
          for (const row of chunk) {
            // create new doc with auto-id
            const ref = doc(collection(db, "items")).withConverter ? doc(collection(db, "items")) : doc(collection(db, "items"));
            batch.set(ref, { ...row, createdAt: new Date().toISOString() }, { merge: false });
          }
          await batch.commit();
          written += chunk.length;
        }
        alert(`Import complete. ${written} items added.`);
        load();
      } catch (err) {
        console.error("bulk import failed", err);
        alert("Import failed — check console");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const exportCsv = () => {
    const cols = ["code","desc","unit","rate","mrp","gst","stock"];
    const lines = [cols.join(",")];
    for (const r of items) {
      const vals = [r.code || "", `"${(r.desc||"").replace(/"/g,'""')}"`, r.unit||"", Number(r.rate||0).toFixed(2), Number(r.mrp||0).toFixed(2), Number(r.gst||0).toFixed(2), Number(r.stock||0)];
      lines.push(vals.join(","));
    }
    CSVLink_download(`items_${new Date().toISOString().slice(0,10)}.csv`, lines.join("\n"));
  };

  return (
    <div className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-lg font-semibold">Item Master</h2>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer bg-gray-100 px-3 py-2 rounded">
            Import Excel
            <input type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
          </label>
          <button onClick={exportCsv} className="px-3 py-2 border rounded">Export CSV</button>
          <button onClick={openCreate} className="px-3 py-2 bg-indigo-600 text-white rounded">+ New Item</button>
        </div>
      </div>

      <div className="border rounded overflow-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="p-2 text-left">Code</th>
              <th className="p-2 text-left">Description</th>
              <th className="p-2 text-left">Unit</th>
              <th className="p-2 text-right">Rate</th>
              <th className="p-2 text-right">MRP</th>
              <th className="p-2 text-right">GST%</th>
              <th className="p-2 text-right">Stock</th>
              <th className="p-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (<tr><td colSpan={8} className="p-4 text-center">Loading…</td></tr>) :
              items.length === 0 ? (<tr><td colSpan={8} className="p-4 text-center text-gray-500">No items</td></tr>) :
              items.map(it => (
                <tr key={it.id} className="border-b hover:bg-gray-50">
                  <td className="p-2">{it.code}</td>
                  <td className="p-2 max-w-xs truncate">{it.desc}</td>
                  <td className="p-2">{it.unit}</td>
                  <td className="p-2 text-right">{"₹ " + Number(it.rate||0).toFixed(2)}</td>
                  <td className="p-2 text-right">{"₹ " + Number(it.mrp||0).toFixed(2)}</td>
                  <td className="p-2 text-right">{Number(it.gst||0).toFixed(2)}%</td>
                  <td className="p-2 text-right">{Number(it.stock||0)}</td>
                  <td className="p-2 text-center">
                    <button onClick={() => openEdit(it)} className="px-2 py-1 text-xs border rounded mr-2">Edit</button>
                    <button onClick={() => handleDelete(it)} className="px-2 py-1 text-xs border rounded text-red-600">Delete</button>
                  </td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md bg-white rounded shadow-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <div className="font-semibold">{editing ? "Edit Item" : "Create Item"}</div>
              <button className="text-red-500" onClick={() => setShowModal(false)} type="button">Close</button>
            </div>

            <div className="space-y-2 text-sm">
              <label><div className="text-xs">Item Code</div><input ref={codeRef} value={code} onChange={e=>setCode(e.target.value)} className="w-full border px-2 py-1" /></label>
              <label><div className="text-xs">Description</div><input value={desc} onChange={e=>setDesc(e.target.value)} className="w-full border px-2 py-1" /></label>
              <div className="grid grid-cols-3 gap-2">
                <label><div className="text-xs">Unit</div><input value={unit} onChange={e=>setUnit(e.target.value)} className="w-full border px-2 py-1" /></label>
                <label><div className="text-xs">Rate</div><input value={rate} onChange={e=>setRate(e.target.value)} className="w-full border px-2 py-1" /></label>
                <label><div className="text-xs">MRP</div><input value={mrp} onChange={e=>setMrp(e.target.value)} className="w-full border px-2 py-1" /></label>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <label><div className="text-xs">GST %</div><input type="number" value={gst} onChange={e=>setGst(Number(e.target.value)||0)} className="w-full border px-2 py-1" /></label>
                <label><div className="text-xs">Stock</div><input type="number" value={stock} onChange={e=>setStock(Number(e.target.value)||0)} className="w-full border px-2 py-1" /></label>
              </div>
            </div>

            <div className="mt-3 flex justify-end gap-2">
              <button onClick={() => setShowModal(false)} className="px-3 py-1 border rounded">Cancel</button>
              <button onClick={save} className="px-3 py-1 bg-blue-600 text-white rounded" disabled={saving}>{saving ? "Saving..." : (editing ? "Update" : "Create")}</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
